import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import * as momentzone from 'moment-timezone';
import { cValidators } from '../../models/validator';
import { IvCertGenericResponseOrganizationUserR } from '../../api/model/ivCertGenericResponseOrganizationUserR';
import { UserService } from '../../api/api/user.service';
import { ProfileService } from '../../core/profile/profile.service';
import { TranslateService } from '../../core/translation/translate.service';
import { LoadingService } from './../../shared/loading/loading.service';
import { MessageService } from './../../shared/message/message.service';
import { Environment } from '../../models/environment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { OrganizationUserP } from '../../api';
import { Router } from '@angular/router';
import { routesList } from '../variables/routes-list';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  providers: [UserService]
})

export class ProfileComponent implements OnInit, AfterViewInit, OnDestroy {

  form: FormGroup = new FormGroup({
    name: new FormControl('', Validators.required),
    nif: new FormControl('', Validators.required),
    pass: new FormControl('', Validators.compose([cValidators.required, cValidators.repeat])),
    passRepeat: new FormControl('', Validators.compose([cValidators.required, cValidators.repeat])),
    email: new FormControl('', Validators.compose([cValidators.required, cValidators.email])),
    phone: new FormControl('', Validators.compose([cValidators.phone])),
    idTimezone: new FormControl('', Validators.required),
  });

  username: string = '';

  guidUser: string = '';

  active: boolean = false;

  timeZones: any[] = [];

  _transParam: boolean = false;

  environment: Environment;

  destroy$: Subject<boolean> = new Subject<boolean>();

  hidePassword: boolean = false;

  requiredMessageError: string = this._translate.instant('form_error_required');

  constructor(
    public _translate: TranslateService,
    private _router: Router,
    private _user: UserService,
    private _loading: LoadingService,
    private _message: MessageService,
    private _profile: ProfileService) {
  }

  ngOnInit(): void {
    this._loading.set(true);
    this.username = this._profile.getUserProperty('username');

    if (this.environment?.features.hideUserPassword) {
      this.hidePassword = true;
      delete this.form.controls.pass;
      delete this.form.controls.passRepeat;
    }

    this.getTimezones();
  }

  ngAfterViewInit(): void {
    if (!this.environment?.features.hideUserPassword) {
      this.checkPasswordRequired();
      this.form.get('pass').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.checkPasswordRequired();
      });

      this.form.get('passRepeat').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.checkPasswordRequired();
      });
    } else {
      this.disablePassValidation();
    }

    this.loadUser();
  }

  ngOnDestroy(): void {
    this.destroy$.unsubscribe();
  }

  getTimezones(): void {
    this.timeZones = [];
    const timezones = momentzone.tz.names();
    for (let i = 0; i < timezones.length; i++) {
      const element = timezones[i];
      const today = new Date();
      const hour = momentzone(today).tz(element).format('Z');
      this.timeZones.push({ value: element, label: element + ' ' + hour });
    }
  }

  loadUser(): void {
    this._user.userGetUser(this.username, this._profile.getToken()).subscribe(
      (response: IvCertGenericResponseOrganizationUserR) => {
        const keys = Object.keys(response.data);
        this.guidUser = response.data.guidUser;
        this.active = response.data.active

        for (const key of keys) {
          switch (key) {
            case 'username':
              this.form?.get('nif').patchValue(response.data[key]);
              break;
            case 'name':
            case 'email':
            case 'phone':
              this.form?.get(key).patchValue(response.data[key]);
              break;
            case 'idTimezone':
              this.form?.get('idTimezone').patchValue(response.data[key]);
              break;
          }
        }
        this._loading.set(false);
      }, (error: HttpErrorResponse) => {
        this._loading.set(false);
        this._message.showError(error);
      });
  }

  checkPasswordRequired(): void {
    if (this.form.get('pass').value || this.form.get('passRepeat').value) {
      this.enablePassValidation();
    } else {
      this.disablePassValidation();
    }
    this.form.get('pass')?.updateValueAndValidity();
    this.form.get('passRepeat')?.updateValueAndValidity();
  }

  disablePassValidation(): void {
    if (this.form.controls.pass && this.form.controls.passRepeat) {
      this.form.controls.pass.clearValidators();
      this.form.controls.passRepeat.clearValidators();
    }
  }

  enablePassValidation(): void {
    this.form.controls.pass.setValidators(Validators.compose([cValidators.required, cValidators.repeat]));
    this.form.controls.passRepeat.setValidators(Validators.compose([cValidators.required, cValidators.repeat]));
  }

  updateFormValue(value: any, key: string): void {
    this.form.patchValue({ [key]: value });
  }

  submitForm(): void {
    this._loading.set(true);
    const value = this.form.value;
    const user: OrganizationUserP = {
      name: value.name,
      username: value.nif,
      email: value.email,
      phone: value.phone ? value.phone : '',
      active: this.active,
      idTimezone: value.idTimezone,
      guidUser: this.guidUser
    };

    if (value.pass) {
      user.password = value.pass;
    }

    this._user.userPutUser(user, this._profile.getToken()).pipe(takeUntil(this.destroy$)).subscribe(response => {
      this._loading.set(false);

      if (response.data) {
        this._message.show(this._translate.instant('visualizarusuario_actualizar_usuario_correcto'), 'success', this._translate.instant('dialog_success_title'));
        this._profile.setUserProperty('email', response.data.email);
        this._profile.setUserProperty('name', response.data.name);
      }
    }, (error: HttpErrorResponse) => {
      this._loading.set(false);
      this._message.showError(error);
    });
  }

  clean(): void {
    this.form.reset();
  }

  return(): void {
    this._router.navigate([routesList.TASKS_PENDING]);
  }

}
